package org.ecolution.webpage.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*

@Page
@Composable
fun CASPage() {
    org.ecolution.webpage.components.layouts.MarkdownLayout("Zirkuläre Baustoffe") {
        org.ecolution.webpage.components.widgets.EcoSpacer()
        org.jetbrains.compose.web.dom.H3(attrs = { id("zirkuläres-bauen-vom-abfall-zum-rohstoff-der-zukunft") }) {
            org.jetbrains.compose.web.dom.Text("Zirkuläres Bauen: Vom Abfall zum Rohstoff der Zukunft")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Der Gebäudesektor ist für rund 37 % der globalen CO₂-Emissionen verantwortlich. Allein in Europa verursacht er über 35 % des gesamten Abfalls. Und selbst wenn Materialien recycelt werden, landen mehr als 60 % im Downcycling – also in minderwertiger Wiederverwendung.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Öffentliche Fördergelder verstärken dieses Problem: Sie fliessen weiterhin in nicht kreislauffähige Materialien* wie EPS-Dämmstoffe.")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("Dabei zählt scheinbar nur eins: schnell CO₂ einsparen, um Klimaziele zu erreichen – statt ganzheitlich und langfristig zu denken.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Doch was passiert am Ende der Lebensdauer dieser erdölbasierten Materialien?")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("Wir stehen dann erneut vor denselben Herausforderungen.")
        }
        org.jetbrains.compose.web.dom.H4(attrs = { id("unsere-modularbeit-zeigt-es-braucht-ein-radikales-umdenken") }) {
            org.jetbrains.compose.web.dom.Text("Unsere Modularbeit zeigt: Es braucht ein radikales Umdenken.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Wir brauchen eine neue Perspektive und ein verändertes Bewusstsein für nachhaltiges, zukunftsfähiges Bauen.")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("Dafür braucht es mehrere zentrale Hebel:")
        }
        org.jetbrains.compose.web.dom.H4(attrs = { id("die-schlüssel-für-echten-wandel") }) {
            org.jetbrains.compose.web.dom.Text("Die Schlüssel für echten Wandel:")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("✅ ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Recycling & Upcycling stärken")
            }
            org.jetbrains.compose.web.dom.Text(" Statt nur zu verwerten, müssen Materialien gleichwertig wiederverwendet werden können – ohne Qualitätsverlust.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("✅ ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Vorausschauend planen")
            }
            org.jetbrains.compose.web.dom.Text(" Bereits in der Planung müssen Materialien so gewählt werden, dass sie sortenrein rückbaubar und wiederverwendbar sind.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("✅ ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Regenerative Materialien sinnvoll ergänzen")
            }
            org.jetbrains.compose.web.dom.Text(" Natürliche Baustoffe wie **Lehm, Holz, Hanf, Stroh, Wolle, Zellulose, Pflanzenfasern oder Myzel sind entweder natürlich, nachwachsend oder kompostierbar und weisen oft sogar eine negative CO₂-Bilanz auf – sie binden Kohlenstoff langfristig. Sie sollten gezielt dort eingesetzt werden, wo sie konventionelle Materialien sinnvoll ergänzen oder ersetzen können.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("✅ ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Konventionelle Baustoffe verbessern")
            }
            org.jetbrains.compose.web.dom.Text(" Beton, Zement, Stahl – sie bleiben weiterhin wichtig. Aber: Abbau und Produktion müssen klimaneutral gestaltet werden, durch neue Verfahren und Technologien, sowie Beimischung recycelte Komponenten oder Ersatz von Inhaltsstoffen, wie z.B. Bindemitteln.")
        }
        org.jetbrains.compose.web.dom.H4(attrs = { id("was-jetzt-entscheidend-ist") }) {
            org.jetbrains.compose.web.dom.Text("Was jetzt entscheidend ist:")
        }
        org.jetbrains.compose.web.dom.Ul {
            org.jetbrains.compose.web.dom.Li {
                org.jetbrains.compose.web.dom.Em {
                    org.jetbrains.compose.web.dom.Text("Fördermittel müssen gezielt in kreislauffähige Materialien fliessen")
                }
                org.jetbrains.compose.web.dom.Text(" – nicht in kurzsichtige Scheinlösungen")
            }
            org.jetbrains.compose.web.dom.Li {
                org.jetbrains.compose.web.dom.Em {
                    org.jetbrains.compose.web.dom.Text("Beim Bau muss der gesamte Lebenszyklus mitgedacht werden")
                }
                org.jetbrains.compose.web.dom.Text(" – von der Planung bis zum Rückbau und der Wiederverwertung")
            }
            org.jetbrains.compose.web.dom.Li {
                org.jetbrains.compose.web.dom.Em {
                    org.jetbrains.compose.web.dom.Text("Kreislaufwirtschaft darf nicht die Ausnahme bleiben")
                }
                org.jetbrains.compose.web.dom.Text(", sondern muss zum neuen Standard werden")
            }
        }
        org.jetbrains.compose.web.dom.H4(attrs = { id("pilotprojekte-die-den-wandel-vormachen") }) {
            org.jetbrains.compose.web.dom.Text("Pilotprojekte, die den Wandel vormachen:")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("🏢 ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Ricola Kräuterzentrum (CH)")
            }
            org.jetbrains.compose.web.dom.Text(" – Stampflehmhalle mit natürlichem Raumklima")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("🏨 ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Hemp Hotel (Südafrika)")
            }
            org.jetbrains.compose.web.dom.Text(" – Höchstes Gebäude aus Hanfbeton, CO₂-negativ")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("🏡 ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("The Bale House (UK)")
            }
            org.jetbrains.compose.web.dom.Text(" – Energieautarkes Besucherzentrum aus Strohballen")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Jetzt ist die Zeit")
            }
            org.jetbrains.compose.web.dom.Text(", den Grundstein für eine zukunftsfähige, regenerative und kreislauffähige Baukultur zu legen.*")
        }
        org.ecolution.webpage.components.widgets.BlogIntro(8)
        org.ecolution.webpage.components.widgets.EcoSpacer()
    }
}
